import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { publicAPI } from '../lib/api';
import {
  ArrowRight,
  CheckCircle,
  Phone,
  Play,
  Clock,
} from 'lucide-react';

// Helper to extract YouTube video ID (supports long-form, short-form, shorts, and embed URLs)
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
};

// Detect embeddable URL — YouTube, Vimeo, Facebook.
const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = getYouTubeEmbedUrl(url);
  if (yt) return yt;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  if (/facebook\.com\/.*\/videos\/|fb\.watch\//i.test(url)) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  }
  return null;
};

const isDirectVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m3u8)(\?.*)?$/i.test(url || '');

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const COUNTDOWN_HOURS = 12; // 12 hour countdown

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    // Get or set the countdown end time in localStorage
    const storageKey = 'medichi_countdown_end';
    let endTime = localStorage.getItem(storageKey);
    
    if (!endTime) {
      // Set new countdown end time (12 hours from now)
      endTime = Date.now() + (COUNTDOWN_HOURS * 60 * 60 * 1000);
      localStorage.setItem(storageKey, endTime.toString());
    } else {
      endTime = parseInt(endTime);
      // If countdown has expired, reset it
      if (Date.now() > endTime) {
        endTime = Date.now() + (COUNTDOWN_HOURS * 60 * 60 * 1000);
        localStorage.setItem(storageKey, endTime.toString());
      }
    }
    
    const calculateTimeLeft = () => {
      const now = Date.now();
      const difference = endTime - now;
      
      if (difference <= 0) {
        // Reset countdown when it expires
        const newEndTime = Date.now() + (COUNTDOWN_HOURS * 60 * 60 * 1000);
        localStorage.setItem(storageKey, newEndTime.toString());
        return { hours: COUNTDOWN_HOURS, minutes: 0, seconds: 0 };
      }
      
      return {
        hours: Math.floor(difference / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };
    
    // Initial calculation
    setTimeLeft(calculateTimeLeft());
    
    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const formatNumber = (num) => num.toString().padStart(2, '0');
  
  return (
    <div className="bg-gradient-to-r from-red-700 via-red-600 to-orange-500 text-white py-4 sm:py-5 px-4" data-testid="countdown-timer">
      <div className="max-w-5xl mx-auto flex flex-col items-center justify-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2">
          <Clock className="h-6 w-6 sm:h-7 sm:w-7 animate-pulse" />
          <span className="font-bold text-base sm:text-xl tracking-wide uppercase">Limited Time Offer!</span>
        </div>
        <span className="text-sm sm:text-lg font-medium text-white/90">Registration closes in:</span>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="bg-black/40 rounded-lg px-3 sm:px-5 py-2 sm:py-3 min-w-[56px] sm:min-w-[80px] text-center">
            <span className="font-mono font-black text-2xl sm:text-4xl lg:text-5xl leading-none">{formatNumber(timeLeft.hours)}</span>
            <span className="text-[10px] sm:text-xs font-semibold block mt-1 text-white/70 tracking-widest">HRS</span>
          </div>
          <span className="font-bold text-2xl sm:text-4xl text-white/60">:</span>
          <div className="bg-black/40 rounded-lg px-3 sm:px-5 py-2 sm:py-3 min-w-[56px] sm:min-w-[80px] text-center">
            <span className="font-mono font-black text-2xl sm:text-4xl lg:text-5xl leading-none">{formatNumber(timeLeft.minutes)}</span>
            <span className="text-[10px] sm:text-xs font-semibold block mt-1 text-white/70 tracking-widest">MIN</span>
          </div>
          <span className="font-bold text-2xl sm:text-4xl text-white/60">:</span>
          <div className="bg-black/40 rounded-lg px-3 sm:px-5 py-2 sm:py-3 min-w-[56px] sm:min-w-[80px] text-center">
            <span className="font-mono font-black text-2xl sm:text-4xl lg:text-5xl leading-none">{formatNumber(timeLeft.seconds)}</span>
            <span className="text-[10px] sm:text-xs font-semibold block mt-1 text-white/70 tracking-widest">SEC</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const JoinLandingPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [founderImage, setFounderImage] = useState(null);
  const [founderImageBroken, setFounderImageBroken] = useState(false);
  const [ctaLink, setCtaLink] = useState('/register');
  const [ctaText, setCtaText] = useState('Join Medichi Tribe Now');
  const [heroDescription, setHeroDescription] = useState('Join 1,000+ Nigerians already earning 6 and 7 figures in commissions monthly with Medichi Homes. Free training. Fast payouts. Zero startup cost.');
  const [heroVideoUrl, setHeroVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await publicAPI.getLandingPage();
      setTestimonials(response.data.testimonials || []);
      if (response.data.founder_image_url) {
        // Handle URL construction properly - only prepend API_BASE if it's a relative path
        const founderUrl = response.data.founder_image_url;
        if (founderUrl.startsWith('http')) {
          setFounderImage(founderUrl);
        } else {
          setFounderImage(`${API_BASE}${founderUrl}`);
        }
      }
      if (response.data.cta_button_link) {
        setCtaLink(response.data.cta_button_link);
      }
      if (response.data.cta_button_text) {
        setCtaText(response.data.cta_button_text);
      }
      if (response.data.hero_description) {
        setHeroDescription(response.data.hero_description);
      }
      if (response.data.hero_video_url) {
        setHeroVideoUrl(response.data.hero_video_url);
      }
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine if link is external or internal
  const isExternalLink = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  const videoTestimonials = testimonials.filter(t => t.type === 'youtube');
  const imageTestimonials = testimonials.filter(t => t.type === 'image');

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="join-landing-page">
      {/* Custom styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
        .font-display { font-family: 'Playfair Display', serif; }
        .font-body { font-family: 'Inter', sans-serif; }
        .blue-gradient { background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #1e40af 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .blue-border { border-color: #1e40af; }
        .blue-bg { background-color: #1e40af; }
        .blue-text { color: #3b82f6; }
      `}</style>

      {/* Spacer for fixed nav */}
      <div className="h-16 sm:h-20" />

      {/* Countdown Timer - Scrolls with page, visible on first load */}
      <CountdownTimer />

      {/* Navigation - Fixed at top */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full blue-bg flex items-center justify-center">
                <span className="text-black font-display text-xl font-bold">M</span>
              </div>
              <span className="font-display text-xl text-white hidden sm:block">Medichi Homes</span>
            </Link>
            <div className="flex items-center gap-3">
              {isExternalLink(ctaLink) ? (
                <a href={ctaLink} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full blue-bg hover:opacity-90 text-white font-semibold px-6">Join Now</Button>
                </a>
              ) : (
                <Link to={ctaLink}>
                  <Button className="rounded-full blue-bg hover:opacity-90 text-white font-semibold px-6">Join Now</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative pt-12 sm:pt-20 pb-20 sm:pb-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1717167398882-15d1cefd22f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMHByb3BlcnR5JTIwbGFuZHxlbnwwfHx8fDE3NzExNDY5NzN8MA&ixlib=rb-4.1.0&q=85"
            alt="Luxury Property"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/90 to-[#0a0a0a]"></div>
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, #1e40af 0%, transparent 50%)'}}></div>
        
        <div className="relative max-w-5xl mx-auto text-center">
          {/* Opportunity Video — shown at the top of the landing page when configured */}
          {heroVideoUrl && (
            <div className="mb-10 rounded-2xl overflow-hidden shadow-2xl shadow-[#1e40af]/30 border border-[#1e40af]/30 bg-black" data-testid="landing-hero-video">
              <div className="relative w-full pt-[56.25%]">
                {getEmbedUrl(heroVideoUrl) ? (
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={getEmbedUrl(heroVideoUrl)}
                    title="The Medichi Homes Opportunity"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="absolute top-0 left-0 w-full h-full"
                    src={heroVideoUrl}
                    controls
                    playsInline
                  />
                )}
              </div>
            </div>
          )}

          <div className="inline-flex items-center gap-2 border border-[#1e40af]/30 bg-[#1e40af]/10 px-4 py-2 rounded-full mb-8">
            <span className="blue-text text-sm font-body font-medium tracking-wide">REAL ESTATE • REAL MONEY • REAL RESULTS</span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight tracking-tight">
            Make <span className="blue-gradient">₦500,000</span> to <span className="blue-gradient">₦5,000,000+</span><br className="hidden sm:block" /> Selling Land
          </h1>
          
          <p className="text-xl sm:text-2xl text-white/60 font-light mb-4 font-body">
            No Experience Needed
          </p>
          
          <p className="text-base sm:text-lg text-white/70 mb-10 max-w-3xl mx-auto leading-relaxed font-body">
            {heroDescription}
          </p>
          
          {isExternalLink(ctaLink) ? (
            <a href={ctaLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="h-14 sm:h-16 px-10 sm:px-14 rounded-full blue-bg hover:opacity-90 text-white font-semibold text-lg sm:text-xl transition-all hover:scale-105 shadow-lg shadow-[#1e40af]/20"
              >
                {ctaText}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </a>
          ) : (
            <Link to={ctaLink}>
              <Button 
                size="lg" 
                className="h-14 sm:h-16 px-10 sm:px-14 rounded-full blue-bg hover:opacity-90 text-white font-semibold text-lg sm:text-xl transition-all hover:scale-105 shadow-lg shadow-[#1e40af]/20"
              >
                {ctaText}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-6 sm:gap-12 mt-16 sm:mt-20 max-w-2xl mx-auto">
            <div className="text-center">
              <p className="text-3xl sm:text-5xl font-display font-bold blue-gradient">12%</p>
              <p className="text-white/50 text-xs sm:text-sm font-body mt-1">Direct Commission</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-5xl font-display font-bold blue-gradient">3%</p>
              <p className="text-white/50 text-xs sm:text-sm font-body mt-1">Indirect Commission</p>
            </div>
            <div className="text-center">
              <p className="text-3xl sm:text-5xl font-display font-bold blue-gradient">₦0</p>
              <p className="text-white/50 text-xs sm:text-sm font-body mt-1">Startup Cost</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#080808]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12">
            Does This Sound Like <span className="blue-gradient">You?</span>
          </h2>
          
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            {/* Content - Takes 3 columns */}
            <div className="lg:col-span-3 space-y-6 text-lg sm:text-xl text-white/70 font-body font-light leading-relaxed">
              <p>
                Are you tired of the <span className="text-white font-medium">same salary that doesn't grow</span>?
              </p>
              <p>
                You've seen people making money in real estate but you <span className="text-white font-medium">don't know where to start</span>.
              </p>
              <p>
                Or maybe you've tried before — joined a company that <span className="text-red-400">didn't pay</span>, 
                <span className="text-red-400"> didn't train you</span>, or <span className="text-red-400">left you hanging</span>...
              </p>
              <p>
                Do you want something <span className="text-white font-medium">real and genuine</span>? Something that <span className="blue-text font-medium">actually works</span>?
              </p>
              
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-2xl sm:text-3xl font-display font-bold">
                  That's exactly why <span className="blue-gradient">Medichi Homes</span> exists.
                </p>
              </div>
            </div>
            
            {/* Image - Takes 2 columns */}
            <div className="lg:col-span-2">
              <img 
                src="https://images.unsplash.com/photo-1633504214759-e1013f422ed7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxibGFjayUyMHdvbWFuJTIwY291bnRpbmclMjBtb25leSUyMGNhc2glMjBzdWNjZXNzfGVufDB8fHx8MTc3MTE0NzQ4NHww&ixlib=rb-4.1.0&q=85"
                alt="Woman with Money"
                className="rounded-3xl w-full h-[300px] sm:h-[350px] object-cover border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS MEDICHI HOMES */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12">
            What is <span className="blue-gradient">Medichi Homes?</span>
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1760320483844-3d808de62def?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwd29tYW4lMjBwcm9mZXNzaW9uYWwlMjByZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBzdWl0fGVufDB8fHx8MTc3MTE0NzUzMXww&ixlib=rb-4.1.0&q=85"
                alt="Professional Woman"
                className="rounded-3xl w-full h-[300px] sm:h-[400px] object-cover border border-white/10"
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2 border border-white/10 rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-white/5 to-transparent">
              <p className="text-lg sm:text-xl text-white/80 font-body font-light leading-relaxed mb-8">
                Medichi Homes is a real estate company that helps <span className="text-white font-medium">everyday Nigerians</span> earn 
                serious money by selling land — <span className="blue-text">without needing capital, experience, or an office</span>.
              </p>
              <div className="space-y-4 text-lg text-white/70 font-body">
                <p>We give you the <span className="text-white font-medium">training</span>.</p>
                <p>We give you the <span className="text-white font-medium">properties</span>.</p>
                <p>We give you the <span className="text-white font-medium">support</span>.</p>
                <p className="text-white font-medium">You bring the hunger to succeed.</p>
              </div>
              <div className="mt-8 p-5 bg-[#1e40af]/10 border border-[#1e40af]/30 rounded-2xl text-center">
                <p className="text-lg sm:text-xl font-display font-bold blue-gradient">
                  When you sell, you earn. Simple.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#080808]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            How It <span className="blue-gradient">Works</span>
          </h2>
          <p className="text-white/50 text-center text-lg font-body mb-16">Four simple steps to start earning</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Register for free", desc: "Takes 2 minutes" },
              { step: 2, title: "Get access", desc: "Free training, marketing materials, and mentorship" },
              { step: 3, title: "Start marketing", desc: "Promote properties to buyers" },
              { step: 4, title: "Get paid — fast", desc: "When your client buys, you earn" },
            ].map((item) => (
              <div key={item.step} className="border border-white/10 rounded-2xl p-6 bg-gradient-to-br from-white/5 to-transparent text-center hover:border-[#1e40af]/50 transition-colors">
                <div className="w-14 h-14 rounded-full blue-bg flex items-center justify-center mx-auto mb-5">
                  <span className="text-black font-display font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-white font-display font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm font-body">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-xl text-white/80 font-body">
              No chasing management for your money. No stories. <span className="blue-text font-semibold">We pay.</span>
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU EARN */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-16">
            What You <span className="blue-gradient">Earn</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Direct Commission */}
            <div className="border border-[#1e40af]/30 rounded-3xl p-8 bg-gradient-to-br from-[#1e40af]/10 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-2xl blue-bg flex items-center justify-center">
                  <span className="text-black font-display font-bold text-3xl">12%</span>
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold text-white">Direct Commission</h3>
                  <p className="blue-text font-body">From your own sales</p>
                </div>
              </div>
              <div className="bg-black/30 rounded-xl p-5">
                <p className="text-white/80 font-body">
                  You sell a plot worth <span className="font-semibold text-white">₦2,000,000</span> — 
                  you earn <span className="font-bold blue-text text-2xl">₦240,000</span>. One sale.
                </p>
              </div>
            </div>

            {/* Indirect Commission */}
            <div className="border border-white/10 rounded-3xl p-8 bg-gradient-to-br from-white/5 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                  <span className="text-white font-display font-bold text-3xl">3%</span>
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold text-white">Indirect Commission</h3>
                  <p className="text-white/60 font-body">From your recruits' sales</p>
                </div>
              </div>
              <div className="bg-black/30 rounded-xl p-5">
                <p className="text-white/80 font-body">
                  You recruit someone. They sell. You earn <span className="font-semibold text-white">3% from their sales</span> too — 
                  <span className="font-medium"> without doing extra work</span>.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 border border-[#1e40af]/30 rounded-2xl p-8 text-center bg-[#1e40af]/5">
            <p className="text-lg sm:text-xl text-white/80 font-body mb-2">
              Some of our marketers have made over <span className="font-bold text-white text-2xl">₦1,000,000</span> in a single month.
            </p>
            <p className="text-xl sm:text-2xl font-display font-bold text-white">
              This is not a "maybe." <span className="blue-gradient">This is happening right now.</span>
            </p>
          </div>
        </div>
      </section>

      {/* WHY MEDICHI HOMES */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#080808]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            Why <span className="blue-gradient">Medichi Homes?</span>
          </h2>
          <p className="text-xl text-white/50 text-center font-body mb-16">Other companies promise. <span className="blue-text font-semibold">We deliver.</span></p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "We allocate land", desc: "Your clients get what they paid for. No wahala. No embarrassment." },
              { title: "We pay fast", desc: "Your commission hits your account. No delays. No excuses." },
              { title: "Free training", desc: "You don't need to know anything about real estate. We teach you everything." },
              { title: "Marketing materials", desc: "Flyers, videos, content. Ready for you to use." },
              { title: "Mentorship & accountability", desc: "You're not alone. We guide you until you succeed." },
              { title: "Supportive community", desc: "Join a tribe of hungry, focused marketers winning together." },
            ].map((item) => (
              <div key={item.title} className="border border-white/10 rounded-2xl p-6 hover:border-[#1e40af]/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full blue-bg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-black" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-white/50 text-sm font-body">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-lg text-white/60 font-body">
              This is <span className="text-red-400 font-medium">not</span> a get-rich-quick scheme.
            </p>
            <p className="text-xl font-display font-bold text-white mt-2">
              This is a real business. And we're looking for <span className="blue-gradient">serious people</span>.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
            Who Is This <span className="blue-gradient">For?</span>
          </h2>
          <p className="text-xl text-white/50 text-center font-body mb-12">This is for you if...</p>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Content */}
            <div className="border border-white/10 rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-white/5 to-transparent">
              <div className="space-y-5">
                {[
                  "You want an extra source of income that can become your main income",
                  "You're a student, job seeker, or worker looking to make more money",
                  "You're already a realtor but tired of unreliable companies",
                  "You have a phone and the willingness to take action",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full blue-bg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-black" />
                    </div>
                    <p className="text-lg text-white/80 font-body">{item}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-lg text-white/70 font-body mb-2">You <span className="text-white font-medium">don't need money</span> to start.</p>
                <p className="text-lg text-white/70 font-body mb-2">You <span className="text-white font-medium">don't need experience</span>.</p>
                <p className="text-xl font-display font-bold text-white">
                  You just need to <span className="blue-gradient">show up and do the work</span>.
                </p>
              </div>
            </div>
            
            {/* Image */}
            <div>
              <img 
                src="https://images.unsplash.com/photo-1739271933159-474db63c2c59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIweW91bmclMjBwcm9mZXNzaW9uYWxzJTIwY2FzdWFsJTIwc3VjY2VzcyUyMHNtaWxpbmd8ZW58MHx8fHwxNzcxMTQ3NTAzfDA&ixlib=rb-4.1.0&q=85"
                alt="Excited Young Professional"
                className="rounded-3xl w-full h-[350px] sm:h-[450px] object-cover border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {(videoTestimonials.length > 0 || imageTestimonials.length > 0) && (
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#080808]">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">
              Real People. <span className="blue-gradient">Real Results.</span>
            </h2>
            <p className="text-xl text-white/50 text-center font-body mb-16">You could be next.</p>

            {/* Video Testimonials */}
            {videoTestimonials.length > 0 && (
              <div className="mb-16">
                <h3 className="text-xl font-display font-semibold text-white/80 mb-8 text-center">Video Testimonials</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videoTestimonials.map((testimonial) => (
                    <div key={testimonial.id} className="border border-white/10 rounded-2xl overflow-hidden bg-black/30">
                      {getYouTubeEmbedUrl(testimonial.url) && (
                        <div className="relative pt-[56.25%]">
                          <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={getYouTubeEmbedUrl(testimonial.url)}
                            title={testimonial.caption || 'Testimonial'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                      {testimonial.caption && (
                        <div className="p-4">
                          <p className="text-sm text-white/60 font-body">{testimonial.caption}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image Testimonials / Screenshots */}
            {imageTestimonials.length > 0 && (
              <div>
                <h3 className="text-xl font-display font-semibold text-white/80 mb-8 text-center">Success Stories & Payment Proofs</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {imageTestimonials.map((testimonial) => (
                    <div key={testimonial.id} className="border border-white/10 rounded-2xl overflow-hidden bg-black/30">
                      <img 
                        src={testimonial.url.startsWith('/api') ? `${API_BASE}${testimonial.url}` : testimonial.url} 
                        alt={testimonial.caption || 'Success story'} 
                        className="w-full h-64 object-cover"
                      />
                      {testimonial.caption && (
                        <div className="p-4">
                          <p className="text-sm text-white/60 font-body">{testimonial.caption}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Placeholder for testimonials if none exist */}
      {videoTestimonials.length === 0 && imageTestimonials.length === 0 && !loading && (
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#080808]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Real People. <span className="blue-gradient">Real Results.</span>
            </h2>
            <p className="text-xl text-white/50 font-body mb-12">You could be next.</p>
            <div className="border border-white/10 rounded-2xl p-12 bg-black/30">
              <Play className="h-16 w-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 font-body">Video testimonials coming soon...</p>
            </div>
          </div>
        </section>
      )}

      {/* ABOUT THE COMPANY */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12">
            About The <span className="blue-gradient">Company</span>
          </h2>
          
          <div className="border border-[#1e40af]/30 rounded-3xl p-8 sm:p-12 bg-gradient-to-br from-[#1e40af]/5 to-transparent">
            {founderImage && !founderImageBroken && (
              <div className="flex justify-center mb-8">
                <img
                  src={founderImage}
                  alt="Ekene Medichi - Founder"
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border-4 border-[#1e40af]/50"
                  onError={() => setFounderImageBroken(true)}
                />
              </div>
            )}
            {(!founderImage || founderImageBroken) && (
              <div className="flex justify-center mb-8">
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-[#1e40af]/50 bg-[#1e40af]/20 flex items-center justify-center">
                  <span className="text-4xl sm:text-5xl font-bold text-[#3b82f6] font-display">EM</span>
                </div>
              </div>
            )}
            <p className="text-lg sm:text-xl text-white/80 font-body font-light leading-relaxed mb-6 text-center">
              Medichi Homes is led by <span className="font-semibold blue-text">Ekene Medichi</span>, a leader committed to building a company 
              where marketers <span className="text-white font-medium">thrive — not struggle</span>.
            </p>
            <p className="text-lg sm:text-xl text-white/80 font-body font-light leading-relaxed mb-8 text-center">
              Our mission is simple: <span className="text-white font-medium">Help Nigerians build wealth through real estate</span> — with integrity, speed, and support.
            </p>
            <div className="bg-black/30 rounded-xl p-6 text-center">
              <p className="text-xl sm:text-2xl font-display font-bold text-white">
                We don't just sell land. <span className="blue-gradient">We build futures.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0a0a0a] to-[#111]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">
            Ready To <span className="blue-gradient">Start?</span>
          </h2>
          
          <div className="space-y-3 text-lg sm:text-xl text-white/60 font-body mb-8">
            <p>Joining is <span className="text-white font-medium">free</span>.</p>
            <p>Training is <span className="text-white font-medium">free</span>.</p>
            <p>Support is <span className="text-white font-medium">free</span>.</p>
          </div>
          
          <p className="text-xl sm:text-2xl text-white font-display mb-10">
            The only thing you need to bring is your <span className="blue-gradient">commitment</span>.
          </p>
          
          <p className="text-lg text-white/50 font-body mb-10">
            Click the button below, fill the form, and welcome to Medichi Tribe.
          </p>
          
          {isExternalLink(ctaLink) ? (
            <a href={ctaLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="h-14 sm:h-16 px-12 sm:px-16 rounded-full bg-white hover:bg-white/90 text-[#1e40af] font-semibold text-lg sm:text-xl shadow-lg transition-all hover:scale-105"
              >
                {ctaText}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </a>
          ) : (
            <Link to={ctaLink}>
              <Button 
                size="lg" 
                className="h-14 sm:h-16 px-12 sm:px-16 rounded-full bg-white hover:bg-white/90 text-[#1e40af] font-semibold text-lg sm:text-xl shadow-lg transition-all hover:scale-105"
              >
                {ctaText}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t border-white/10 text-center">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full blue-bg flex items-center justify-center">
              <span className="text-black font-display text-sm font-bold">M</span>
            </div>
            <span className="font-display text-lg text-white">Medichi Homes</span>
          </div>
          <p className="text-white/30 text-sm font-body">
            © {new Date().getFullYear()} Medichi Homes. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default JoinLandingPage;
